import { Component, ViewChild } from '@angular/core';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import {
  PoBreadcrumb,
  PoNotificationService,
  PoUploadComponent,
  PoUploadFileRestrictions
} from '@po-ui/ng-components';
import { catchError, forkJoin, of } from 'rxjs';
import { RelatorioConvenioService } from './relatorio-convenio.service';

type ArquivoUpload = { name: string; rawFile?: File };
type Resultado = HttpResponse<Blob> | HttpErrorResponse;

@Component({
  selector: 'app-relatorio-convenio',
  templateUrl: './relatorio-convenio.component.html',
  styleUrls: ['./relatorio-convenio.component.css'],
  standalone: false
})
export class RelatorioConvenioComponent {
  @ViewChild('uploadPdf') uploadPdf?: PoUploadComponent;

  enviando = false;

  readonly urlPlaceholder = 'https://po-ui.io';
  readonly pdfRestrictions: PoUploadFileRestrictions = {
    allowedExtensions: ['.pdf'],
    maxFileSize: 50 * 1024 * 1024
  };

  readonly breadcrumb: PoBreadcrumb = {
    items: [
      { label: 'Home', link: '/home' },
      { label: 'RH' },
      { label: 'Relatório de convênio médico' }
    ]
  };

  constructor(
    private readonly convenioService: RelatorioConvenioService,
    private readonly poNotificationService: PoNotificationService
  ) {}

  gerar(): void {
    if (this.enviando) return;

    const pdf = this.arquivo();
    if (!pdf) {
      this.poNotificationService.warning('Envie o PDF da Amil.');
      return;
    }

    this.enviando = true;
    forkJoin({
      individuais: this.convenioService.individuais(pdf).pipe(catchError((err: HttpErrorResponse) => of(err))),
      resumo: this.convenioService.resumo(pdf).pipe(catchError((err: HttpErrorResponse) => of(err)))
    }).subscribe({
      next: resultado => {
        void this.entregar(resultado.individuais, resultado.resumo);
      },
      error: () => {
        this.enviando = false;
        this.poNotificationService.error('Não foi possível gerar os relatórios.');
      }
    });
  }

  private async entregar(individuais: Resultado, resumo: Resultado): Promise<void> {
    const erroIndividual = await this.baixarSeOk(individuais, 'convenio-amil.zip');
    if (!erroIndividual) {
      await new Promise(resolve => setTimeout(resolve, 400));
    }
    const erroResumo = await this.baixarSeOk(resumo, 'convenio-amil-resumo-rh.xlsx');
    this.enviando = false;

    if (!erroIndividual && !erroResumo) {
      this.poNotificationService.success('Planilhas dos funcionários e resumo do RH baixados.');
      this.uploadPdf?.clear();
      return;
    }

    if (erroIndividual) this.poNotificationService.error(erroIndividual);
    if (erroResumo) this.poNotificationService.error(erroResumo);
    if (!erroIndividual || !erroResumo) {
      this.poNotificationService.warning('O outro relatório foi baixado.');
    }
  }

  private async baixarSeOk(resultado: Resultado, padrao: string): Promise<string | null> {
    if (resultado instanceof HttpErrorResponse) {
      return this.mensagem(resultado);
    }

    const blob = resultado.body;
    if (!blob || blob.size === 0) {
      return 'O relatório voltou vazio.';
    }

    this.baixar(blob, this.nomeArquivo(resultado.headers.get('content-disposition'), padrao));
    return null;
  }

  private arquivo(): File | null {
    const files = (this.uploadPdf?.currentFiles ?? []) as ArquivoUpload[];
    return files.map(item => item.rawFile).find((file): file is File => !!file) ?? null;
  }

  private baixar(blob: Blob, nome: string): void {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
  }

  private nomeArquivo(contentDisposition: string | null, padrao: string): string {
    const match = /filename\*?=(?:UTF-8''|"?)([^";]+)/i.exec(contentDisposition ?? '');
    if (!match) return padrao;
    return decodeURIComponent(match[1].replace(/"/g, ''));
  }

  private async mensagem(err: HttpErrorResponse): Promise<string> {
    const body = err?.error;
    if (body instanceof Blob) {
      const texto = await body.text();
      try {
        const json = JSON.parse(texto) as { error?: string; Error?: string };
        return json.error || json.Error || texto || 'Não foi possível gerar o relatório.';
      } catch {
        return texto || 'Não foi possível gerar o relatório.';
      }
    }
    if (typeof body === 'string' && body.trim()) return body;
    if (body && typeof body === 'object') {
      const json = body as { error?: string; Error?: string; title?: string };
      return json.error || json.Error || json.title || 'Não foi possível gerar o relatório.';
    }
    if (err.status === 403) return 'Você não tem permissão para gerar este relatório.';
    return 'Não foi possível gerar o relatório.';
  }
}
